/**
 * Helper functions for load management - extracted for testability
 */
import { InstallationStatus } from "@/config/constants";
import { LoadFilters, LoadListItem, LoadInstallation } from "@/types/loads";

/**
 * Checks if installation status is a "handled" state (no longer pending)
 */
export function isHandledStatus(status: string): boolean {
  return ["completed", "partial", "skipped", "cancelled"].includes(status);
}

/**
 * Returns Tailwind classes for the position indicator background
 */
export function getLoadPositionColor(status: InstallationStatus | string): string {
  switch (status) {
    case "completed":
      return "bg-green-500 text-white";
    case "partial":
      return "bg-orange-500 text-white";
    case "skipped":
      return "bg-red-500 text-white";
    case "cancelled":
      return "bg-gray-500 text-white";
    default:
      return "bg-primary text-primary-foreground";
  }
}

/**
 * Installation status icon type mapping
 */
export type StatusIconType = "check" | "dashed" | "skip" | "x" | null;

/**
 * Returns icon type for installation status (for UI mapping)
 */
export function getInstallationStatusIconType(status: InstallationStatus | string): StatusIconType {
  switch (status) {
    case "completed":
      return "check";
    case "partial":
      return "dashed";
    case "skipped":
      return "skip";
    case "cancelled":
      return "x";
    default:
      return null;
  }
}

/**
 * Calculates status counts from installations array
 */
export interface StatusCounts {
  completed: number;
  partial: number;
  skipped: number;
  pending: number;
}

export function calculateStatusCounts(installations: LoadInstallation[]): StatusCounts {
  return installations.reduce(
    (acc, inst) => {
      const status = inst.status;
      if (status === "completed") acc.completed++;
      else if (status === "partial") acc.partial++;
      else if (status === "skipped") acc.skipped++;
      else if (status === "pending" || status === "in_progress") acc.pending++;
      return acc;
    },
    { completed: 0, partial: 0, skipped: 0, pending: 0 }
  );
}

/**
 * Filters loads based on filter criteria
 */
export function filterLoads(loads: LoadListItem[], filters: LoadFilters): LoadListItem[] {
  return loads.filter((load) => {
    // Filter by date
    if (filters.date) {
      const loadDate = new Date(load.load_date);
      if (loadDate.toDateString() !== filters.date.toDateString()) {
        return false;
      }
    }

    // Filter by status
    if (filters.status !== "all" && load.status !== filters.status) {
      return false;
    }

    // Filter by installer
    if (filters.installerId !== "all" && load.installer_id !== filters.installerId) {
      return false;
    }

    return true;
  });
}

/**
 * Checks if any filters are active
 */
export function hasActiveFilters(filters: LoadFilters): boolean {
  return (
    filters.date !== undefined ||
    filters.status !== "all" ||
    filters.installerId !== "all"
  );
}
